import { getSortParam } from '@disco/api/helpers'
import api from '@disco/api/server'
import type { AddToDiscoParams } from 'types/api'
import {
  AGGREGATED_SEARCH_QUERY_SUCCESS,
  BRIEF_PANE_UPDATE,
  EXPLORER_CLOSE_PANE,
  EXPLORER_UPDATE,
  LIBRARY_SELECT,
  LIBRARY_USER_DEFAULT_LOAD,
  PANE_LOAD,
  PANE_LOAD_FAIL,
  PANE_LOAD_SUCCESS,
  PANE_RECENTS_PUSH,
  PANE_UPDATE,
  PANES_UPDATE,
  SIDEBAR_CLOSE,
  TRACK_LOAD_BATCH,
  TRACKS_UPDATE,
  USER_SETTINGS_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { __REACT_NATIVE__ } from '@disco/constants/environment'
import { TRACK_NESTING } from '@disco/constants/FeatureTypes'
import {
  BRIEF_INBOX,
  BRIEFS,
  CUSTOM_CHANNEL,
  PERSONAL_LIBRARY,
  TEAM_LIBRARY,
} from '@disco/constants/GroupTypes'
import { GROUP, PLAYLIST } from '@disco/constants/ItemTypes'
import { NESTED_TRACK_ALREADY_IN_PLAYLIST } from '@disco/constants/Nesting'
import {
  BRIEF_LIST_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
} from '@disco/constants/PaneTypes'
import type { SIMILARITY_FEEDBACK } from '@disco/constants/SimilarityFeedbackTypes'
import type { SORT, SORT_DIRECTION } from '@disco/constants/SortTypes'
import {
  PLAYLIST_DATE_SORT,
  SORT_DIRECTION_ASC,
  SORT_DIRECTION_DESC,
  TITLE_SORT,
} from '@disco/constants/SortTypes'
import type { ViewType } from '@disco/constants/ViewTypes'
import {
  ALBUM_VIEW,
  ARTIST_VIEW,
  BRIEF_ALL_VIEW,
  BRIEF_INBOX_VIEW,
  BRIEF_VIEWS,
  DEFAULT_VIEW,
  PLAYLIST_VIEW,
  TRACK_VIEW,
} from '@disco/constants/ViewTypes'
import type { State as ExplorerState } from '@disco/state-management/reducers/explorer'
import { getSelectedLibrary } from '@disco/state-management/selectors/app'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getExplorerFirstPane,
  getExplorerPane,
} from '@disco/state-management/selectors/explorer'
import {
  getAutoTaggingGroup,
  getDefaultLibrary,
  getGroup,
  getGroups,
  getPersonalInbox,
  getNestedGroup,
} from '@disco/state-management/selectors/groups'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import {
  getAdjancentPlaylistTracks,
  getTrack,
  getTracksByNestingParentId,
} from '@disco/state-management/selectors/tracks'
import {
  getPlaylistExclusionList,
  isShowPrivacyPolicyDialog,
} from '@disco/state-management/selectors/user'
import {
  describeMultipleGroups,
  getPersonalLibrary,
  getTeamLibrary,
  isChannelShared,
  itemRequireAddingToTeamLib,
  sortFolders,
} from '@disco/utils/groups'
import { normalizeV2Track } from '@disco/utils/search-page'
import { isInternalUrlRedirect } from '@disco/utils/hostnames'
import { mapPaneToMixpanelEventObject } from '@disco/utils/mixpanel'
import {
  getStemsFromTracksArray,
  removeStemsFromTrack,
} from '@disco/utils/nesting'
import {
  pushUINotification,
  startPendingUINotification,
} from '@disco/utils/notifications'
import { getPaneSortSettingsWithDefaults } from '@disco/utils/sorting'
import { mediaItemsToString } from '@disco/utils/string-utils'
import { getNestedTracksCount, getStemsCount } from '@disco/utils/tracks'
import captureError from '@disco/utils/captureError'
import get from 'lodash/get'
import uniqBy from 'lodash/uniqBy'
import NavigationService from '@disco/state-management/NavigationService'
import type { ExplorerPane } from 'types/explorer'
import type {
  Group,
  GroupId,
  IndentedGroup,
  GroupWithNesting,
} from 'types/group'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { GetState, Thunk } from 'types/redux'
import type { Track, TrackId } from 'types/track'
import type { Settings, User } from 'types/user'
import type { UINotification } from 'types/notification'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import {
  updatePlaylists,
  loadPlaylistVersions,
} from '@disco/state-management/actions/playlist-actions'
import { createNewShortlist } from '@disco/state-management/actions/ShortlistActions'
import {
  fetchNestedTracks,
  loadBatch,
  loadNestedTracks,
  mapResponseToReducerObjects,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import { fetchTrackMetadata } from '@disco/state-management/actions/ServerActions'
import {
  Brief,
  BriefListApiItem,
  BriefProject,
  MixedBriefListApiItem,
} from 'types/brief'
import { loadBrief, setBriefs } from '../reducers/entities/briefs'
import { setBriefProjects } from '../reducers/entities/briefProjects'

type BriefResponse = {
  results: MixedBriefListApiItem[]
}

/**
 * Updates a single explorer pane with new data.
 * @param pane - The explorer pane to update
 * @param actionPrefix - Optional prefix for the action type
 * @returns Action object with updated pane data
 */
export function updatePane(
  pane: ExplorerPane,
  actionPrefix = '',
): {
  result: ExplorerPane
  type: string
} {
  return {
    type: `${actionPrefix}${PANE_UPDATE}`,
    result: pane,
  }
}
/**
 * Updates multiple explorer panes with new data.
 * @param panes - Array of explorer panes to update
 * @param actionPrefix - Optional prefix for the action type
 * @returns Action object with updated panes data
 */
export function updatePanes(
  panes: ExplorerPane[],
  actionPrefix = '',
): {
  results: Array<ExplorerPane>
  type: string
} {
  return {
    type: `${actionPrefix}${PANES_UPDATE}`,
    results: panes,
  }
}

/**
 * Updates brief in explorer panes with new data.
 * @param brief - The brief object with updated data
 * @param actionPrefix - Optional prefix for the action type
 * @returns Action object with updated brief data
 */
export function updateBriefInPanes(
  brief: Brief,
  actionPrefix = '',
): {
  brief: Brief
  type: string
} {
  return {
    type: `${actionPrefix}${BRIEF_PANE_UPDATE}`,
    brief,
  }
}

const getGroupHeader = (pane: Partial<ExplorerPane>): string => {
  if (pane.groupIds) return JSON.stringify(pane.groupIds)
  if (pane.groupId) return JSON.stringify([pane.groupId])
  return ''
}

/**
 * Loads the next page of content for a pane (pagination).
 * @param pane - The explorer pane to load the next page for
 * @param actionPrefix - Optional prefix for the action type
 * @returns Thunk that loads the next page of pane content
 */
export function loadNextPanePage(
  pane: ExplorerPane,
  actionPrefix = '',
): (
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
) => Promise<any> | void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (!pane) return

    // Search results panes paginate the v2 search endpoint with a cursor
    // instead of following a legacy `next` URL.
    if (pane.type === SEARCH_RESULTS_PANE && pane.searchQueryV2) {
      if (!pane.next) return
      return api
        .searchV2({ ...pane.searchQueryV2, cursor: pane.next })
        .then(response => {
          const result = {
            results: response.hits.map(normalizeV2Track),
            next: response.pagination.cursor,
          }
          const obj = mapResponseToReducerObjects(dispatch, getState, result)
          dispatch(
            updatePane(
              {
                ...pane,
                results: [...(pane.results ?? []), ...obj.results],
                next: result.next,
              },
              actionPrefix,
            ),
          )
        })
        .catch(error => {
          dispatch(sendGenericError(error))
        })
    }

    const groupHeader = getGroupHeader(pane)
    // url for tracks are placed in different property
    const nextUrl =
      pane.type === 'playlist' ? pane.tracks_info?.next : pane.next
    if (!nextUrl) return
    return api
      .getPane(groupHeader, nextUrl)
      .then(async response => {
        let result

        if (pane.view === ARTIST_VIEW) {
          result = {
            ...response,
            results: response.results.map(artist => ({
              ...artist,
              type: 'artist',
            })),
          }
        } else if (pane.type === BRIEF_LIST_PANE) {
          const results = await dispatch(
            setBriefsAndBriefProjects(response.results),
          )

          const newPane = {
            ...pane,
            results: [...(pane.results || []), ...results],
            next: response.next,
          }
          return dispatch(updatePane(newPane, actionPrefix))
        } else {
          result = response
        }

        const obj = mapResponseToReducerObjects(dispatch, getState, result)
        // TODO: track pane should be treated too
        let newPane: typeof pane | null = null

        if (pane.type === 'playlist') {
          newPane = {
            ...pane,
            tracks_info: result.tracks_info,
            tracks: obj.tracks,
          }
        } else {
          newPane = {
            ...pane,
            results: [...(pane.results ?? []), ...obj.results],
            next: result.next,
          }
        }

        dispatch(updatePane(newPane, actionPrefix))
      })
      .catch(error => {
        dispatch(sendGenericError(error))
      })
  }
}

/**
 * Toggles a track's presence in the user's library from a shared channel.
 * @param track - The track to toggle in library
 * @param group - The shared channel group context
 * @returns Thunk that toggles the track in library with channel context
 */
export function toggleSharedChannelTrackInLibrary(
  track: Track,
  group: Group,
): (dispatch: any, getState: GetState) => void {
  return _toggleTrackInLibrary(track, group)
}

/**
 * Toggles a track's presence in the user's library.
 * @param track - The track to toggle in library
 * @returns Thunk that adds or removes the track from the user's library
 */
export function toggleTrackInLibrary(
  track: Track,
): (dispatch: any, getState: GetState) => void {
  return _toggleTrackInLibrary(track)
}

function _toggleTrackInLibrary(
  track: Track,
  group?: Group,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const library: Group | null | undefined = getPersonalLibrary(
      getState().user.groups,
    )
    const teamLib: Group | null | undefined = getTeamLibrary(
      getState().user.groups,
    )
    if (!library) return
    const isStarred: boolean = track.groups.includes(library.url)

    if (!isStarred) {
      const addGroups: Group[] = [library]

      if (teamLib && !track.groups.includes(teamLib.url)) {
        addGroups.push(teamLib)
      }

      dispatch(alterTrackGroups([track], addGroups, []))
    } else {
      dispatch(alterTrackGroups([track], [], [library]))
    }

    const mixpanelEvent: string = !isStarred
      ? 'Favourite track'
      : 'Unfavourite track'

    const extraContext = {}
    if (group && isChannelShared(group)) {
      extraContext['Context'] = 'Shared Channels'
      extraContext['Channel ID'] = group.id
      extraContext['Channel Owner'] = group.business?.id
    }
    dispatch(
      sendMixpanelEvent(mixpanelEvent, {
        Name: track.name,
        Album: track.album,
        Artist: track.artist,
        ...extraContext,
      }),
    )
  }
}
/**
 * Copies content to Disco with the specified item name.
 * @param itemName - The name of the item to copy
 */
export function copyToDisco(
  itemName: string,
  objectUrl: string,
  destination: {
    groupIds?: GroupId[]
    playlistIds?: PlaylistId[]
  },
  originGroupId?: GroupId,
  isLibrary: boolean = false,
  includeNestedTracks: boolean = true,
  includeStems: boolean = true,
  sync: boolean = true,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const finishPendingUINotification = startPendingUINotification(
      `Copying ‘${itemName}’...`,
    )

    try {
      dispatch(sendMixpanelEvent('Copy to DISCO'))
      await api.addToDisco(objectUrl, {
        add_to_groups: destination.groupIds || [],
        add_to_playlists: destination.playlistIds || [],
        origin_group_id: originGroupId,
        include_nested_tracks: includeNestedTracks,
        include_stems: includeStems,
        sync,
      })

      if (destination.groupIds) {
        if (destination.groupIds?.length === 1) {
          const firstGroupId = destination.groupIds?.[0]
          const group = firstGroupId
            ? getGroup(getState(), {
                groupId: firstGroupId,
              })
            : null

          if (group)
            finishPendingUINotification({
              autoDismiss: 10,
              level: 'success',
              message: isLibrary
                ? `'${itemName}' has been added to your DISCO!`
                : `'${itemName}' has been added to the channel '${group.name}'!`,
              action: isLibrary
                ? undefined
                : {
                    label: 'Open channel',
                    callback: () => dispatch(loadLibrary(group)),
                  },
            })
        } else {
          finishPendingUINotification({
            autoDismiss: 10,
            level: 'success',
            message: isLibrary
              ? `'${itemName}' has been added to your DISCO!`
              : `'${itemName}' has been added to ${destination.groupIds.length} channels!`,
          })
        }
      }

      if (destination.playlistIds) {
        if (destination.playlistIds?.length === 1) {
          const firstPlaylistId = destination.playlistIds?.[0]
          const playlist = firstPlaylistId
            ? getPlaylist(getState(), {
                id: firstPlaylistId,
              })
            : null

          if (playlist && firstPlaylistId !== undefined)
            finishPendingUINotification({
              autoDismiss: 10,
              level: 'success',
              message: `'${itemName}' has been added to the playlist '${playlist.name}'!`,
              action: {
                label: 'Open playlist',
                callback: () => dispatch(openPlaylistRoute(firstPlaylistId)),
              },
            })
        } else {
          finishPendingUINotification({
            autoDismiss: 10,
            level: 'success',
            message: `'${itemName}' has been added to ${destination.playlistIds.length} playlists!`,
          })
        }
      }
    } catch (error) {
      finishPendingUINotification({
        message: `There was an issue copying '${itemName}'. Please try again, and if the issue persists contact support.`,
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, { description: 'Error copying item to DISCO' })
    }
  }
}
/**
 * Copies a playlist to DISCO and opens it in shortlists.
 * @param playlist - The playlist to copy
 * @returns Thunk that copies the playlist and opens it in shortlists
 */
export function copyPlaylistToDiscoAndOpenInShortlists(
  playlist: Playlist,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const finishPendingUINotification = startPendingUINotification(
      `Copying playlist ‘${playlist.name}’...`,
    )

    try {
      const objectUrl = playlist?.view_playlist_url
      const teamLibrary: Group | null | undefined = getTeamLibrary(
        getState().user.groups,
      )
      const teamLibraryId = teamLibrary?.id
      if (!teamLibraryId || !objectUrl) return
      const response = await api.addToDisco(objectUrl, {
        add_to_groups: [teamLibraryId],
        sync: true,
        include_nested_tracks: true,
        include_stems: true,
      })
      const createdPlaylistId = response?.playlist_ids[0]
      if (!createdPlaylistId) throw new Error('Error creating playlist')
      const newPlaylist = await dispatch(
        loadPlaylistVersions(createdPlaylistId),
      )
      finishPendingUINotification({
        level: 'success',
        title: 'Playlist copied!',
        message: `The playlist ‘${playlist.name}’ has been successfully copied.`,
        autoDismiss: 10,
      })
      dispatch(createNewShortlist(newPlaylist))
    } catch (error) {
      finishPendingUINotification({
        message: `There was an issue copying the playlist. Please try again, and if the issue persists contact support.`,
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, { description: 'Error copying playlist to DISCO' })
    }
  }
}

const getTrackCountText = (numberOfTracks: number): string =>
  numberOfTracks > 1 ? `${numberOfTracks} tracks` : 'track'

export function copyBulkTracksToDisco(
  tracks: Track[],
  originGroupId: GroupId,
): (dispatch: any, getState: GetState) => Promise<Track[] | void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const finishPendingUINotification = startPendingUINotification(
      `Copying ${getTrackCountText(tracks.length)} to DISCO...`,
    )
    try {
      const teamLibrary: Group | null | undefined = getTeamLibrary(
        getState().user.groups,
      )
      const teamLibraryId = teamLibrary?.id
      if (!teamLibraryId || !tracks.length) return

      const responses = await Promise.all(
        tracks.map(track =>
          api.addToDisco(track.stream_url, {
            add_to_groups: [teamLibraryId],
            origin_group_id: originGroupId,
            sync: true,
            include_nested_tracks: true,
            include_stems: true,
          }),
        ),
      )
      const importedTrackIds = responses.flatMap(response => response.track_ids)

      if (!importedTrackIds) throw new Error('Error uploading tracks')

      await Promise.all(
        importedTrackIds.map(trackId => dispatch(fetchTrackMetadata(trackId))),
      )
      const importedTracks = importedTrackIds
        .map(trackId =>
          getTrack(getState(), {
            id: trackId,
          }),
        )
        .filter(track => track !== null && track !== undefined)

      finishPendingUINotification({
        level: 'success',
        title: `${getTrackCountText(importedTracks.length)} copied!`,
        message: `The tracks have been successfully copied.`,
        autoDismiss: 10,
      })
      return importedTracks
    } catch (error) {
      finishPendingUINotification({
        message: `There was an issue copying ${getTrackCountText(
          tracks.length,
        )}. Please try again, and if the issue persists contact support.`,
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, { description: 'Error copying tracks to DISCO' })
    }
  }
}

/**
 * Copies playlist tracks to DISCO without creating a new playlist.
 * @param playlist - The playlist whose tracks to copy
 * @param originGroupId - Optional origin group ID
 * @returns Thunk that copies the playlist tracks and returns them
 */
export function copyPlaylistTracksToDisco(
  playlist: Playlist,
  originGroupId?: GroupId,
): (dispatch: any, getState: GetState) => Promise<Track[]> {
  return async (dispatch, getState) => {
    const finishPendingUINotification = startPendingUINotification(
      `Copying playlist ‘${playlist.name}’...`,
    )

    try {
      const objectUrl = playlist?.view_playlist_url
      if (!objectUrl) return

      const params: AddToDiscoParams = {
        include_nested_tracks: true,
        include_stems: true,
        sync: true,
        keep_destination_playlist: false,
      }

      if ((originGroupId ?? null) !== null) {
        params.origin_group_id = originGroupId
      }

      const response = await api.addToDisco(objectUrl, params)

      const importedPlaylistTrackIds = response?.track_ids
      if (!importedPlaylistTrackIds) throw new Error('Error creating playlist')

      for (const trackId of importedPlaylistTrackIds) {
        await dispatch(fetchTrackMetadata(trackId))
      }
      const importedPlaylistTracks = importedPlaylistTrackIds.map(trackId =>
        getTrack(getState(), {
          id: trackId,
        }),
      )

      finishPendingUINotification({
        level: 'success',
        title: 'Playlist copied!',
        message: `The ‘${playlist.name}’ has been successfully copied.`,
        autoDismiss: 10,
      })

      return importedPlaylistTracks
    } catch (error) {
      finishPendingUINotification({
        message: `There was an issue copying the playlist. Please try again, and if the issue persists contact support.`,
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, { description: 'Error copying playlist to DISCO' })
    }
  }
}

/**
 * Copies a playlist to a specific channel.
 * @param playlist - The playlist to copy
 * @param targetGroup - The target channel group
 * @param originSourceId - Optional origin source ID
 * @returns Thunk that copies the playlist to the specified channel
 */
export function copyPlaylistToChannel(
  playlist: Playlist,
  targetGroup: Group,
  originSourceId?: GroupId,
): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    const finishPendingUINotification = startPendingUINotification(
      `Copying playlist ‘${playlist.name}’...`,
    )

    try {
      const result = await api.addToDisco(playlist.view_playlist_url, {
        add_to_groups: [targetGroup.id],
        origin_group_id: originSourceId,
        sync: true,
        include_nested_tracks: true,
        include_stems: true,
      })
      // fetch imported playlist
      const importedPlaylistId = result?.playlist_ids[0]

      if (importedPlaylistId) {
        finishPendingUINotification({
          message: `Playlist added to ${targetGroup.name}!`,
          level: 'success',
          autoDismiss: 10,
          action: targetGroup.id
            ? {
                label: 'View playlist',
                callback: () => dispatch(loadLibrary(targetGroup)),
              }
            : undefined,
        })
      }
    } catch (error) {
      finishPendingUINotification({
        message:
          'There was an issue adding that playlist to channel. Please try again, and if the issue persists contact support.',
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, {
        description: 'Error in importing playlist to channel',
      })
    }
  }
}
/**
 * Adds an aggregated search track to DISCO.
 * @param options - Configuration options
 * @param options.track - The track to add
 * @param options.originGroupId - Optional origin group ID
 * @param options.includeNestedTracks - Whether to include nested tracks
 * @param options.includeStems - Whether to include stems
 * @returns Thunk that adds the track to DISCO and returns the imported track
 */
export function addAggregatedTrackToDisco({
  track,
  originGroupId,
  includeNestedTracks,
  includeStems,
}: {
  track: Track
  originGroupId?: GroupId
  includeNestedTracks?: boolean
  includeStems?: boolean
}): (dispatch: any, getState: GetState) => Promise<Track | null | undefined> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      const teamLib: Group | null | undefined = getTeamLibrary(
        getState().user.groups,
      )
      const personalInbox: Group | null | undefined = getPersonalInbox(
        getState(),
      )
      const library: Group | null | undefined = getPersonalLibrary(
        getState().user.groups,
      )

      if (library && teamLib && personalInbox) {
        const result = await api.addToDisco(track.stream_url, {
          sync: true,
          origin_group_id: originGroupId,
          add_to_groups: [library.id, teamLib.id, personalInbox.id],
          include_nested_tracks: includeNestedTracks ?? false,
          include_stems: includeStems ?? false,
        })

        dispatch(
          sendMixpanelEvent('Favourite track', {
            Name: track.name,
            Album: track.album,
            Artist: track.artist,
            // Properties for event #10 from https://disco-team.atlassian.net/browse/DISCO-5985
            'Track ID': track.id,
            Catalog: track.origin?.source_library?.id,
            'Catalog Business': track.origin?.business?.id,
            Context: 'Aggregated search',
          }),
        )
        pushUINotification({
          message: 'Track added to your DISCO!',
          level: 'success',
          autoDismiss: 10,
        })

        // sync track groups
        const importedTrackId = result.track_ids[0]
        await dispatch(fetchTrackMetadata(importedTrackId))
        const importedTrack = getTrack(getState(), {
          id: importedTrackId,
        })
        // swap agg track with imported track
        const {
          aggregatedSearch: { searchResults, nextPageUrl },
        } = getState().search
        dispatch({
          type: AGGREGATED_SEARCH_QUERY_SUCCESS,
          params: {
            aggregatedResults: searchResults.map(aggTrack => {
              if (aggTrack.id === track.id) {
                return importedTrack
              }

              return aggTrack
            }),
            aggregatedNextPageUrl: nextPageUrl,
          },
        })
        return importedTrack
      }
    } catch (error) {
      pushUINotification({
        message:
          'There was an issue adding that track to your DISCO. Please try again, and if the issue persists contact support.',
        level: 'error',
        autoDismiss: 10,
      })
      dispatch(sendGenericError(error))
      captureError(error, {
        description: 'Error in importing aggregated track to DISCO',
      })
    }
  }
}
/**
 * Adds tracks to the user's personal and team libraries.
 * @param tracks - Array of tracks to add
 * @param disableNotifications - Whether to disable UI notifications
 * @returns Thunk that adds tracks to libraries
 */
export function addTracksToLibrary(
  tracks: Track[],
  disableNotifications = false,
): (dispatch: any, getState: GetState) => any | null {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const library = getPersonalLibrary(getState().user.groups)
    const teamLib = getTeamLibrary(getState().user.groups)
    if (library && teamLib)
      return dispatch(
        alterTrackGroups(tracks, [library, teamLib], [], {
          disableNotifications,
        }),
      )
    return null
  }
}
/**
 * Adds a track to multiple groups.
 * @param track - The track to add
 * @param chosenGroups - Array of groups to add the track to
 * @returns Thunk that adds the track to the specified groups
 */
export function addTrackToGroups(
  track: Track,
  chosenGroups: Group[],
): (dispatch: any) => any {
  return (dispatch: (...args: Array<any>) => any) => {
    return dispatch(alterTrackGroups([track], chosenGroups))
  }
}
/**
 * Removes a track from a specific group.
 * @param track - The track to remove
 * @param group - The group to remove the track from
 * @returns Thunk that removes the track from the group
 */
export function removeTrackFromGroup(
  track: Track,
  group: Group,
): (dispatch: any) => any {
  return (dispatch: (...args: Array<any>) => any) => {
    return dispatch(alterTrackGroups([track], [], [group]))
  }
}
/**
 * Moves a track from one group to another.
 * @param track - The track to move
 * @param sourceGroup - The source group
 * @param targetGroup - The target group
 * @returns Thunk that moves the track between groups
 */
export function moveTrack(
  track: Track,
  sourceGroup: Group,
  targetGroup: Group,
): (dispatch: any) => any {
  return (dispatch: (...args: Array<any>) => any) => {
    return dispatch(alterTrackGroups([track], [targetGroup], [sourceGroup]))
  }
}
/**
 * Removes stems from tracks and returns updated tracks with stems as separate items.
 * @param tracks - Array of tracks to process
 * @returns Function that processes tracks and returns updated tracks with stems separated
 */
export const removeStemsFromTracks = (
  tracks: Track[],
): ((dispatch: any, getState: GetState) => Array<Track>) => {
  return (_dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const updatedTracks: Track[] = tracks.flatMap((track: Track) => {
      const nestedTracks = getTracksByNestingParentId(getState(), {
        id: track.id,
      })
      const stems = getStemsFromTracksArray(nestedTracks)
      return [
        removeStemsFromTrack(track),
        ...stems.map((track: Track) => ({
          ...track,
          nesting_label: null,
          nesting_parent_id: null,
          nesting_position: null,
        })),
      ]
    })
    return updatedTracks
  }
}
/**
 * Alters track group membership by adding or removing tracks from groups.
 * @param tracks - Array of tracks to alter
 * @param addGroups - Groups to add tracks to
 * @param removeGroups - Groups to remove tracks from
 * @param options - Optional configuration
 * @param options.disableNotifications - Whether to disable UI notifications
 * @param options.includeStems - Whether to include stems
 * @returns Thunk that alters track group membership
 */
export function alterTrackGroups(
  tracks: Track[],
  addGroups: Group[],
  removeGroups?: Group[],
  options?: {
    disableNotifications?: boolean
    includeStems?: boolean
  },
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const groups: Group[] = getGroups(getState())
    const uploadedTracks: Track[] = tracks.filter(t => t.id)
    const inboxTracks: Track[] = uploadedTracks.filter(item =>
      itemRequireAddingToTeamLib(item.groups, groups),
    )
    const isNestingEnabled: boolean = isFeatureEnabled(getState(), {
      featureName: TRACK_NESTING,
    })

    if (
      isNestingEnabled &&
      options?.includeStems === undefined &&
      inboxTracks.some(track => getStemsCount(track) > 0)
    ) {
      return NavigationService.push('/import-stems', {
        state: {
          type: 'track',
          tracks,
          addGroups,
          removeGroups,
          options,
        },
      })
    }

    try {
      let results

      if (inboxTracks.length > 0) {
        results = await api.importInboxTracks(
          uploadedTracks,
          addGroups,
          removeGroups,
          options?.includeStems,
        )
      } else {
        results = await api.alterTrackGroups(
          uploadedTracks,
          addGroups,
          removeGroups,
        )
      }

      if (!options?.disableNotifications) {
        if (removeGroups && removeGroups.length > 0 && addGroups.length === 0) {
          // show notification on pure remove action
          const description = describeMultipleGroups(removeGroups)
          pushUINotification({
            message: `Removed from ${description}!`,
            // Examples for grep:
            // - Removed from channel!
            // - Removed from folder!
            // - Removed from inbox!
            level: 'success',
          })
        }

        if (addGroups && addGroups.length > 0) {
          const description = describeMultipleGroups(addGroups)
          const actionWord =
            removeGroups && removeGroups.length > 0 ? 'Moved' : 'Added'
          pushUINotification({
            message: `${actionWord} to ${description}!`,
            // Examples for grep:
            // - Added to 2 folders!
            // - Added to folder!
            // - Added to channel!
            level: 'success',
          })
        }
      }

      let updatedTracks: Track[] = uploadedTracks.map(t => {
        const updatedTrack = results.find(r => r.id === t.id)
        return { ...t, groups: updatedTrack ? updatedTrack.groups : t.groups }
      })

      if (options?.includeStems === false) {
        updatedTracks = dispatch(removeStemsFromTracks(updatedTracks))
      }

      dispatch({
        type: TRACK_LOAD_BATCH,
        list: updatedTracks,
      })
    } catch (error) {
      const isRemove = Boolean(removeGroups?.length)
      const tracksInStringWithId = mediaItemsToString(tracks, 'id')
      const tracksInString = mediaItemsToString(tracks)
      let intercomMsg = `I’m having trouble adding ${tracksInStringWithId} to ${mediaItemsToString(
        addGroups,
        'id',
      )}.`
      let message = `There was an error adding the ${tracksInString} to ${mediaItemsToString(
        addGroups,
      )}. Click |${JSON.stringify({
        retryMessage: 'here',
      })}| to try again. If you’re still having trouble, |${JSON.stringify({
        contactUsLink: 'let us know.',
        intercomMsg,
      })}|`

      if (isRemove) {
        intercomMsg = `I’m having trouble removing ${tracksInStringWithId} from ${mediaItemsToString(
          removeGroups,
          'id',
        )}.`
        message = `There was an error removing the ${tracksInString} from ${mediaItemsToString(
          removeGroups,
        )}. Click |${JSON.stringify({
          retryMessage: 'here',
        })}| to try again. If you’re still having trouble, |${JSON.stringify({
          contactUsLink: 'let us know.',
          intercomMsg,
        })}|`
      }

      pushUINotification({
        message: message,
        retry: () =>
          dispatch(alterTrackGroups(tracks, addGroups, removeGroups, options)),
        level: 'error',
        dismissible: 'button',
      })
      return dispatch(sendGenericError(error))
    }
  }
}
/**
 * Alters playlist group membership by adding or removing playlists from groups.
 * @param playlists - Array of playlists to alter
 * @param addGroups - Groups to add playlists to
 * @param removeGroups - Groups to remove playlists from
 * @param options - Optional configuration
 * @param options.disableNotifications - Whether to disable UI notifications
 * @param options.alterTracks - Whether to alter tracks as well
 * @param options.includeStems - Whether to include stems
 * @returns Thunk that alters playlist group membership
 */
export function alterPlaylistGroups(
  playlists: Playlist[],
  addGroups: Group[],
  removeGroups?: Group[],
  options?: {
    disableNotifications?: boolean
    alterTracks?: boolean
    includeStems?: boolean
  },
): (
  dispatch: any,
  getState: GetState,
) =>
  | Promise<unknown>
  | Promise<{
      error: any
    }> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const groups: Group[] = getGroups(getState())
    const inboxPlaylists: Playlist[] = playlists.filter(item =>
      itemRequireAddingToTeamLib(item.groups, groups),
    )
    const isNestingEnabled: boolean = isFeatureEnabled(getState(), {
      featureName: TRACK_NESTING,
    })

    if (
      isNestingEnabled &&
      options?.includeStems === undefined &&
      inboxPlaylists.some(playlist => playlist.has_nested_stem_tracks)
    ) {
      return NavigationService.push('/import-stems', {
        state: {
          type: 'playlist',
          playlists,
          addGroups,
          removeGroups,
          options,
        },
      })
    }

    try {
      let results

      if (inboxPlaylists.length > 0) {
        results = await api.importInboxPlaylists(
          playlists,
          addGroups,
          removeGroups,
          options?.alterTracks,
          options?.includeStems,
        )
      } else {
        results = await api.alterPlaylistGroups(
          playlists,
          addGroups,
          removeGroups,
          options?.alterTracks,
        )
      }

      // move playlists
      const updatedPlaylists = playlists.map(p => {
        const updatedPlaylist = results.find(r => r.id === p.playlist_id)
        return {
          ...p,
          groups: updatedPlaylist ? updatedPlaylist.groups : p.groups,
        }
      })

      if (!options?.disableNotifications) {
        const hasRemovedPlaylistGroups = removeGroups && removeGroups.length > 0

        if (hasRemovedPlaylistGroups && addGroups.length === 0) {
          // show notification on pure remove action
          const description = describeMultipleGroups(removeGroups)
          pushUINotification({
            message: `Removed from ${description}!`,
            // Examples for grep:
            // - Removed from channel!
            // - Removed from folder!
            // - Removed from inbox!
            level: 'success',
          })
        }

        if (addGroups && addGroups.length > 0) {
          const description = describeMultipleGroups(addGroups)
          const actionWord = hasRemovedPlaylistGroups ? 'Moved' : 'Added'
          pushUINotification({
            message: `${actionWord} to ${description}!`,
            // Examples for grep:
            // - Added to 2 folders!
            // - Added to folder!
            // - Added to channel!
            level: 'success',
          })
        }
      }

      if (options?.includeStems === false) {
        let updatedTracks = playlists.reduce(
          (tracks, playlist) =>
            uniqBy(
              [
                ...tracks,
                ...getAdjancentPlaylistTracks(getState(), {
                  playlistId: playlist.playlist_id,
                }),
              ],
              'id',
            ),
          [],
        )
        updatedTracks = updatedTracks.filter(track => getStemsCount(track) > 0)
        updatedTracks = dispatch(removeStemsFromTracks(updatedTracks))
        dispatch({
          type: TRACK_LOAD_BATCH,
          list: updatedTracks,
        })
      }

      dispatch(updatePlaylists(updatedPlaylists))
    } catch (error) {
      const isRemove = Boolean(removeGroups?.length)
      const playlistsInStringWithId = mediaItemsToString(
        playlists,
        'playlist_id',
      )
      const playlistsInString = mediaItemsToString(playlists)
      let intercomMsg = `I’m having trouble adding ${playlistsInStringWithId} to ${mediaItemsToString(
        addGroups,
        'id',
      )}.`
      let message = `There was an error adding the ${playlistsInString} to ${mediaItemsToString(
        addGroups,
      )}. Click |${JSON.stringify({
        retryMessage: 'here',
      })}| to try again. If you’re still having trouble, |${JSON.stringify({
        contactUsLink: 'let us know.',
        intercomMsg,
      })}|`

      if (isRemove) {
        intercomMsg = `I’m having trouble removing ${playlistsInStringWithId} from ${mediaItemsToString(
          removeGroups,
          'id',
        )}.`
        message = `There was an error removing the ${playlistsInString} from ${mediaItemsToString(
          removeGroups,
        )}. Click |${JSON.stringify({
          retryMessage: 'here',
        })}| to try again. If you’re still having trouble, |${JSON.stringify({
          contactUsLink: 'let us know.',
          intercomMsg,
        })}|`
      }

      pushUINotification({
        message,
        retry: () =>
          dispatch(
            alterPlaylistGroups(playlists, addGroups, removeGroups, options),
          ),
        level: 'error',
        dismissible: 'button',
      })
      dispatch(sendGenericError(error))
      return {
        error,
      }
    }
  }
}
/**
 * Removes a playlist from a specific group.
 * @param playlist - The playlist to remove
 * @param group - The group to remove the playlist from
 * @returns Thunk that removes the playlist from the group
 */
export function removePlaylistFromGroup(
  playlist: Playlist,
  group: Group,
): (dispatch: any) => any {
  return (dispatch: (...args: Array<any>) => any) => {
    return dispatch(alterPlaylistGroups([playlist], [], [group]))
  }
}
/**
 * Moves a playlist from one group to another.
 * @param playlist - The playlist to move
 * @param sourceGroup - The source group
 * @param targetGroup - The target group
 * @returns Thunk that moves the playlist between groups
 */
export function movePlaylist(
  playlist: Playlist,
  sourceGroup: Group,
  targetGroup: Group,
): (dispatch: any) => any {
  return (dispatch: (...args: Array<any>) => any) => {
    return dispatch(
      alterPlaylistGroups([playlist], [targetGroup], [sourceGroup]),
    )
  }
}
/**
 * Toggles a playlist's presence in the user's library.
 * @param playlist - The playlist to toggle
 * @returns Thunk that adds or removes the playlist from the user's library
 */
export function togglePlaylistInLibrary(
  playlist: Playlist,
): (dispatch: any, getState: GetState) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const library: Group | null | undefined = getPersonalLibrary(
      getState().user.groups,
    )
    const teamLib: Group | null | undefined = getTeamLibrary(
      getState().user.groups,
    )
    if (!library) return
    const isStarred: boolean = playlist.groups.includes(library.url)

    if (!isStarred) {
      const addGroups: Group[] = [library]

      if (teamLib && !playlist.groups.includes(teamLib.url)) {
        addGroups.push(teamLib)
      }

      await dispatch(
        alterPlaylistGroups([playlist], addGroups, [], {
          alterTracks: true,
        }),
      )
    } else {
      await dispatch(alterPlaylistGroups([playlist], [], [library]))
    }

    const mixpanelEvent: string = !isStarred
      ? 'Favourite playlist'
      : 'Unfavourite playlist'
    dispatch(
      sendMixpanelEvent(mixpanelEvent, {
        Name: playlist.name,
      }),
    )
  }
}
/**
 * Updates the explorer state with new data.
 * @param explorer - Partial explorer state to update
 * @param actionPrefix - Optional prefix for the action type
 * @returns Action object with updated explorer state
 */
export function updateExplorer(
  explorer: Partial<ExplorerState>,
  actionPrefix = '',
): {
  result: any | number | Array<ExplorerPane> | Array<string>
  type: string
} {
  return {
    type: `${actionPrefix}${EXPLORER_UPDATE}`,
    result: explorer,
  }
}
/**
 * Closes an explorer pane at the specified index.
 * @param index - The index of the pane to close
 * @param actionPrefix - Optional prefix for the action type
 * @returns Action object for closing the pane
 */
export function closePane(
  index: number,
  actionPrefix = '',
): {
  type: string
  index: number
} {
  return {
    type: `${actionPrefix}${EXPLORER_CLOSE_PANE}`,
    index,
  }
}
/**
 * Moves the active pane focus in the specified direction.
 * @param direction - Direction to move (positive for right, negative for left)
 * @returns Thunk that updates the active pane index
 */
export function moveActivePane(
  direction: number,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { activePane, panes } = getState().explorer
    const newActivePane = activePane + direction

    if (newActivePane > -1 && newActivePane < panes.length) {
      dispatch(
        updateExplorer({
          activePane: newActivePane,
        }),
      )
    }
  }
}
/**
 * Loads a library and its panes into the explorer.
 * @param library - The library group to load
 * @param actionPrefix - Optional prefix for the action type
 * @param isShared - Whether the library is shared
 * @returns Thunk that loads the library and navigates to it
 */
export function loadLibrary(
  library: Group | IndentedGroup,
  actionPrefix = '',
  isShared = false,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    let extraContext = {}
    if (isShared) {
      extraContext = {
        Context: 'Shared Channels',
        'Channel ID': library.id,
        'Channel Owner': library.business?.id,
      }
    }
    dispatch({
      type: LIBRARY_SELECT,
      library: library,
    })
    library.panes?.forEach(pane => {
      dispatch(loadPane(pane, actionPrefix, extraContext))
    })
    NavigationService.push('/')
  }
}

/**
 * Loads the briefs list pane.
 * @returns Thunk that loads the briefs pane
 */
export function loadBriefs(): (dispatch: any) => Promise<void> {
  return (dispatch: (...args: Array<any>) => any) => {
    const url = api.getPaneUrl({ groupType: BRIEFS, view: BRIEF_ALL_VIEW })
    return dispatch(
      loadPane({
        type: BRIEF_LIST_PANE,
        url: url,
        index: 0,
        title: 'Briefs',
      }),
    )
  }
}

/**
 * Loads the user's default library based on their settings.
 * @param user - The user whose default library to load
 * @returns Thunk that loads the user's default library
 */
export function loadUserDefaultLibrary(user: User): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    // mobile app doesn't need to load user default library
    // it loads recents instead
    if (__REACT_NATIVE__) return
    if (!user.libraries || user.libraries.length === 0) return
    if (isInternalUrlRedirect()) return
    const startGroup = get(user, 'settings.explorer_default_view', TEAM_LIBRARY)
    const library = user.libraries.find(lib => lib.group_type === startGroup)
    if (!library) return
    dispatch({
      type: LIBRARY_USER_DEFAULT_LOAD,
      library,
    })
    library.panes.forEach(pane => {
      dispatch(loadPane(pane))
    })
  }
}

function getSortParams(
  paneSortParams,
  view,
  inputSortParam,
  inputSortDirection,
) {
  if (!inputSortParam || !inputSortDirection) {
    let sortParam = paneSortParams.playlistSortParam

    if (view === ARTIST_VIEW) {
      sortParam = paneSortParams.artistSortParam
    }

    if (view === TRACK_VIEW) {
      sortParam = paneSortParams.trackSortParam
    }

    let sortDirection = paneSortParams.playlistSortDirection

    if (view === ARTIST_VIEW) {
      sortDirection = paneSortParams.artistSortDirection
    }

    if (view === TRACK_VIEW) {
      sortDirection = paneSortParams.trackSortDirection
    }

    return {
      sortParam,
      sortDirection,
    }
  }

  return {
    sortParam: inputSortParam,
    sortDirection: inputSortDirection,
  }
}

function getSettingsUpdate(view, groupType, sortParam, sortDirection) {
  if ([DEFAULT_VIEW, PLAYLIST_VIEW, ALBUM_VIEW].includes(view)) {
    if (groupType === CUSTOM_CHANNEL) {
      return {
        channel_playlist_sort_param: sortParam,
        channel_playlist_sort_direction: sortDirection,
      }
    }

    return {
      group_playlist_sort_param: sortParam,
      group_playlist_sort_direction: sortDirection,
    }
  }

  if (view === TRACK_VIEW) {
    return {
      track_sort_param: sortParam,
      track_sort_direction: sortDirection,
    }
  }

  return null
}

let abortController: AbortController

type PaneViewOptions = {
  sortParam?: SORT
  sortDirection?: SORT_DIRECTION
  tags?: string[]
  searchTerm?: string
}

/**
 * Switches the view type of an explorer pane (e.g., from playlist to track view).
 * @param pane - The pane to switch view for
 * @param view - The new view type
 * @param options - Optional view configuration
 * @param options.sortParam - Sort parameter
 * @param options.sortDirection - Sort direction
 * @param options.tags - Filter tags
 * @param options.searchTerm - Search term
 * @param actionPrefix - Optional prefix for the action type
 * @returns Thunk that switches the pane view
 */
export function switchPaneView(
  pane: ExplorerPane | null | undefined,
  view: ViewType,
  options: PaneViewOptions = {},
  actionPrefix = '',
): (dispatch: any, getState: GetState) => void {
  const {
    sortParam: inputSortParam,
    sortDirection: inputSortDirection,
    searchTerm,
    tags,
  } = options
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (!pane) return

    if (abortController) {
      abortController.abort()
    }

    abortController = new AbortController()
    const abortSignal = abortController.signal

    const groupHeader = getGroupHeader(pane)
    const selectedLibrary = getSelectedLibrary(getState())
    const isPersonalLibrary = selectedLibrary?.group_type === PERSONAL_LIBRARY
    const { settings } = getState().user
    const paneSortParams = getPaneSortSettingsWithDefaults(pane, settings)
    const { sortParam, sortDirection } = getSortParams(
      paneSortParams,
      view,
      inputSortParam,
      inputSortDirection,
    )
    const group = getState().groups.groups.find(g => g.id === pane.groupId)
    const playlistsToExclude = getPlaylistExclusionList(getState())
    const groupType = group ? group.group_type : undefined

    api
      .getPaneView(
        {
          groupHeader,
          groupType,
          view,
          sortParam,
          sortDirection,
          excludePlaylistTypes: playlistsToExclude,
          tagsFilter: tags,
          isPersonalLibrary,
          searchTerm,
        },
        { abortSignal },
      )
      .then(response => {
        if (abortSignal.aborted) return

        if (!__REACT_NATIVE__) {
          const { explorer } = getState()
          if (view !== explorer.panes[explorer.activePane].view) return
        }

        let result

        if (view === ARTIST_VIEW) {
          result = {
            ...response,
            results: response.results.map(artist => ({
              ...artist,
              type: 'artist',
            })),
          }
        } else if (BRIEF_VIEWS.includes(view)) {
          result = {
            results: response.results.map(data => ({
              ...data,
              type: data.type ?? 'brief',
            })),
            next: response.next,
          }
        } else {
          result = response
        }

        let searchResultWithFolders

        if (BRIEF_VIEWS.includes(view)) {
          dispatch(setBriefsAndBriefProjects(result.results))
          searchResultWithFolders = result
        } else {
          const folderSortParam =
            view === TRACK_VIEW ? paneSortParams.playlistSortParam : sortParam
          const folderSortDirection =
            view === TRACK_VIEW
              ? paneSortParams.playlistSortDirection
              : sortDirection
          searchResultWithFolders = includeFoldersInSearchResult(
            dispatch,
            getState,
            pane,
            result,
            folderSortParam,
            folderSortDirection,
            searchTerm,
          )
          // update user settings
          const settingsUpdate = getSettingsUpdate(
            view,
            groupType,
            sortParam,
            sortDirection,
          )
          // trigger settings update only if something really changed
          const id = pane.groupId
          const settingsNeedUpdate =
            settingsUpdate &&
            id &&
            Object.keys(settingsUpdate).some(
              key =>
                settings.group_sort_settings?.[id]?.[key] !==
                settingsUpdate[key],
            )

          if (settingsNeedUpdate && typeof id !== 'undefined') {
            dispatch(
              updateUserSettingsUnsafe({
                settings: {
                  group_sort_settings: {
                    [id]: {
                      ...settings.group_sort_settings?.[id],
                      ...settingsUpdate,
                    },
                  },
                },
              }),
            )
          }
        }

        let sortChange = {}

        if ([DEFAULT_VIEW, PLAYLIST_VIEW, ALBUM_VIEW].includes(view)) {
          sortChange = {
            playlistSortParam: sortParam,
            playlistSortDirection: sortDirection,
          }
        }

        if (view === TRACK_VIEW) {
          sortChange = {
            trackSortParam: sortParam,
            trackSortDirection: sortDirection,
          }
        }

        if (view === ARTIST_VIEW) {
          sortChange = {
            artistSortParam: sortParam,
            artistSortDirection: sortDirection,
          }
        }

        return dispatch({
          type: `${actionPrefix}${PANE_LOAD_SUCCESS}`,
          result: searchResultWithFolders,
          pane: {
            ...pane,
            url: api.getPaneUrl({
              groupType,
              view,
              sortParam,
              sortDirection,
              excludePlaylistTypes: playlistsToExclude,
              tagsFilter: tags,
              searchTerm,
            }),
            view: view,
            ...sortChange,
            searchTerm,
          },
        })
      })
      .catch(error => {
        if (abortSignal.aborted) return

        if (!__REACT_NATIVE__) {
          const { explorer } = getState()
          if (view !== explorer.panes[explorer.activePane].view) return
        }

        return dispatch({
          type: `${actionPrefix}${PANE_LOAD_FAIL}`,
          error: error,
          pane,
        })
      })
    dispatch({
      type: `${actionPrefix}${PANE_LOAD}`,
      pane,
      view,
    })
  }
}
function getSearchNestedFolders(
  folders: GroupWithNesting[],
  searchTerm: string,
) {
  const paneFolders: (GroupWithNesting & { type: 'group' })[] = []
  folders.forEach(group => {
    paneFolders.push({ ...group, type: 'group' })
    group?.folders?.forEach(folder => {
      if (
        folder?.name &&
        folder?.name.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        paneFolders.push({ ...folder, type: 'group' })
      }
    })
  })

  return paneFolders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )
}

function includeFoldersInSearchResult(
  dispatch: (...args: Array<any>) => any,
  getState: GetState,
  pane: Partial<ExplorerPane>,
  result,
  sortParam: SORT | null | undefined,
  sortDirection: SORT_DIRECTION | null | undefined,
  searchTerm?: string,
) {
  let correctedResult = result

  if (pane.type === TRACK_PANE) {
    const { playlist_versions } = result

    if (
      playlist_versions &&
      playlist_versions.length > 0 &&
      sortParam &&
      sortDirection
    ) {
      const lessThan = sortDirection === SORT_DIRECTION_ASC ? -1 : 1
      const moreThan = sortDirection === SORT_DIRECTION_ASC ? 1 : -1
      let sortedPlaylists = playlist_versions

      switch (sortParam) {
        case PLAYLIST_DATE_SORT:
          sortedPlaylists = playlist_versions.slice().sort((a, b) => {
            return (
              (new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()) *
              moreThan
            )
          })
          break

        case TITLE_SORT:
          sortedPlaylists = playlist_versions.slice().sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return lessThan
            if (a.name.toLowerCase() > b.name.toLowerCase()) return moreThan
            return 0
          })
          break

        default:
          break
      }

      correctedResult = { ...result, playlist_versions: sortedPlaylists }
    }
  }

  const searchResult = mapResponseToReducerObjects(
    dispatch,
    getState,
    correctedResult,
  )
  if (pane.type !== SEARCH_RESULTS_PANE) return searchResult

  let folders = getState()
    .groups.groups.filter(group => group.parent === pane.groupId)
    .map(group => {
      return { ...group, type: 'group' }
    })

  if (searchTerm) {
    folders = folders
      ?.map(
        item =>
          item.type === 'group' &&
          getNestedGroup(getState(), { groupId: item.id }),
      )
      .filter(Boolean)
    folders = getSearchNestedFolders(folders, searchTerm)
  }

  if (sortParam && sortDirection) {
    folders = sortFolders(folders, sortParam, sortDirection)
  }

  return {
    ...searchResult,
    results: [...folders, ...searchResult.results],
    folders: folders,
  }
}

/**
 * Loads content into an explorer pane.
 * @param pane - The pane configuration to load
 * @param actionPrefix - Optional prefix for the action type
 * @param mixpanelContext - Optional mixpanel context
 * @returns Thunk that loads the pane content
 */
export function loadPane( // TODO: add type for different pane base objects such as
  // {
  //   url: `${window.host}/api/search/`,
  //   name: name,
  //   index: pane.index + 1,
  //   groupId: id,
  //   groupType: CUSTOM_CHANNEL,
  //   view: DEFAULT_VIEW,
  //   type: SEARCH_RESULTS_PANE,
  // }
  pane: Record<string, any>,
  actionPrefix = '',
  mixpanelContext?: Record<string, any>,
): (dispatch: any, getState: GetState) => Promise<unknown> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const isTrackNestingEnabled = isFeatureEnabled(getState(), {
      featureName: TRACK_NESTING,
    })
    const { sidebarOpen, launchedOnMobileDevice } = getState().app
    if (launchedOnMobileDevice && sidebarOpen)
      dispatch({
        type: SIDEBAR_CLOSE,
      })
    const groupHeader = getGroupHeader(pane)
    const { settings } = getState().user
    let paneSortSettings

    if (pane.groupId && pane.type === SEARCH_RESULTS_PANE) {
      paneSortSettings = getPaneSortSettingsWithDefaults(pane, settings)
    } else {
      const firstPane = getExplorerFirstPane(getState())

      if (firstPane && firstPane.groupType && firstPane.groupId) {
        paneSortSettings = getPaneSortSettingsWithDefaults(firstPane, settings)
      } else {
        paneSortSettings = {
          playlistSortParam: null,
          playlistSortDirection: null,
        }
      }
    }

    const { playlistSortParam, playlistSortDirection } = paneSortSettings
    let url = pane.url

    const getSign = url => (url.indexOf('?') > -1 ? '&' : '?')

    let sortInjectedPane = pane

    if (pane.groupId && pane.type === SEARCH_RESULTS_PANE) {
      // TODO: drop per pane sort settings, just use user settings
      const { view } = pane

      if (!view || view === DEFAULT_VIEW || view === PLAYLIST_VIEW) {
        if (playlistSortParam) {
          const sortParam = getSortParam(
            pane.groupType,
            playlistSortParam,
            playlistSortDirection,
          )

          url += `${getSign(url)}sort=${sortParam}`
        } else {
          url += `${getSign(url)}`
        }

        const sortChange = {
          playlistSortParam,
          playlistSortDirection,
        }
        sortInjectedPane = {
          ...pane,
          ...sortChange,
        }
      }

      const playlistsToExclude = getPlaylistExclusionList(getState())

      if (playlistsToExclude && playlistsToExclude.length > 0) {
        const excludeList = playlistsToExclude.join(',')
        url += `&exclude_playlist_types=[${excludeList}]`
      }
    }

    dispatch({
      type: `${actionPrefix}${PANE_LOAD}`,
      pane: sortInjectedPane,
    })

    if (pane.type === PLAYLIST_PANE && isTrackNestingEnabled) {
      url += `${getSign(url)}include_nested_tracks=1`
    }

    if (pane.type === BRIEF_LIST_PANE) {
      const originalResponse = await api.getPane(groupHeader, url)
      const results = await dispatch(
        setBriefsAndBriefProjects(originalResponse.results),
      )
      const briefResponse: BriefResponse = {
        results,
      }

      return dispatch({
        type: `${actionPrefix}${PANE_LOAD_SUCCESS}`,
        result: briefResponse,
        pane: {
          ...sortInjectedPane,
          groupType: BRIEFS,
          next: originalResponse.next,
          count: originalResponse.count,
        },
        meta: {
          mixpanel: mapPaneToMixpanelEventObject(pane),
        },
      })
    }
    return api
      .getPane(groupHeader, url)
      .then(async origResult => {
        if (
          pane.type === TRACK_PANE &&
          isTrackNestingEnabled &&
          origResult.nesting_parent_id
        ) {
          // we are opening a track that is nested, check if we need to load
          // the parent so we can display it
          const mainTrackId = origResult.nesting_parent_id
          await dispatch(loadNestedTracks(mainTrackId))
        }

        if (pane.type === PLAYLIST_PANE && isTrackNestingEnabled) {
          const mainTracks = origResult.tracks.filter(
            track => getNestedTracksCount(track) > 0,
          )
          dispatch(loadBatch(mainTracks, false))
          const mainTrackIds = mainTracks.map(({ id }) => id)
          await dispatch(fetchNestedTracks(mainTrackIds))
        }

        return origResult
      })
      .then(result => {
        // TODO: reformat in async/await style
        // Start quick fix for the task https://app.asana.com/0/1199373740409044/1200012390439807/f
        const idsResult = {}
        const filterItems: Record<string, any>[] = []
        let filterSuccess = false

        if (result.results) {
          try {
            result.results.forEach(item => {
              if (item.type !== PLAYLIST) {
                return filterItems.push(item)
              }

              if (!idsResult[item.playlist_id]) {
                idsResult[item.playlist_id] = item
                filterItems.push(item)
              }
            })
            filterSuccess = true
          } catch (error) {
            console.log(error)
          }
        }

        // end

        const searchResultWithFolders = includeFoldersInSearchResult(
          dispatch,
          getState,
          pane,
          filterSuccess ? { ...result, results: filterItems } : result,
          playlistSortParam,
          playlistSortDirection,
        )

        const { panes } = getState().explorer

        // check if we need to fire breadcrumb following event
        if (pane.type === PLAYLIST_PANE) {
          if (
            panes[pane.index - 1] &&
            panes[pane.index - 1].type === TRACK_PANE
          ) {
            // we need to fire breadcrumb event only once
            // so check if conditions for firing event were met previously
            let eventPreviouslyFired = false

            for (let i = 0; i < pane.index - 2; i++) {
              if (
                panes[i].type === TRACK_PANE &&
                i + 1 < panes.length &&
                panes[i + 1].type === PLAYLIST_PANE
              ) {
                eventPreviouslyFired = true
                break
              }
            }

            if (!eventPreviouslyFired) {
              dispatch(sendMixpanelEvent('Access breadcrumb trail in explorer'))
            }
          }
        }
        return dispatch({
          type: `${actionPrefix}${PANE_LOAD_SUCCESS}`,
          result: searchResultWithFolders,
          pane: sortInjectedPane,
          meta: {
            mixpanel: mapPaneToMixpanelEventObject(pane, mixpanelContext),
          },
        })
      })
      .catch(error => {
        return dispatch({
          type: `${actionPrefix}${PANE_LOAD_FAIL}`,
          error: error,
          pane: sortInjectedPane,
        })
      })
  }
}
/**
 * Loads a pane with search results from the current search state.
 * @param pane - The pane to load with search results
 * @param actionPrefix - Optional prefix for the action type
 * @returns Thunk that loads the pane with search results
 */
export function loadPaneWithSearchResults( // TODO: add type for different pane base objects such as
  // {
  //   index: 0,
  //   type: SEARCH_RESULTS_PANE,
  //   searching: true,
  //   searchError: false,
  //   name: 'Searching...',
  //   groupIds: groupHeader,
  //   next: null,
  // },
  pane: ExplorerPane,
  actionPrefix = '',
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // need to reset currently selected library when user opens search results
    // to not to cause dnd object having old library in the drag source props
    dispatch({
      type: LIBRARY_SELECT,
      library: null,
    })
    const search = getState().search
    const mixpanelData = mapPaneToMixpanelEventObject(pane)

    dispatch({
      type: `${actionPrefix}PANE_LOAD_SUCCESS`,
      result: {
        results: search.list,
        aggregatedSearchResults: search.aggregatedSearch
          ? {
              searchResults: search.aggregatedSearch.searchResults,
              next: search.aggregatedSearch.nextPageUrl,
              queryV2: search.aggregatedSearch.queryV2,
            }
          : undefined,
      },
      pane: pane,
      meta: mixpanelData
        ? {
            mixpanel: mixpanelData,
          }
        : undefined,
    })
  }
}
const placeholderSearchPane = {
  index: 0,
  activeItem: `pane/0`,
  type: SEARCH_RESULTS_PANE,
  name: '',
}
/**
 * Opens a track route in the explorer.
 * @param trackId - The ID of the track to open
 * @returns Thunk that opens the track route
 */
export function openTrackRoute(
  trackId: TrackId,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const pane = {
      name: '',
      index: 1,
      type: TRACK_PANE,
    }
    dispatch({
      type: PANE_LOAD,
      pane: placeholderSearchPane,
    })
    api
      .getTrack(trackId)
      .then(result => {
        const track = mapResponseToReducerObjects(dispatch, getState, result)
        dispatch({
          type: PANE_LOAD_SUCCESS,
          result: {
            results: [track],
          },
          pane: { ...placeholderSearchPane, name: track.name, url: track.url },
        })
        dispatch({
          type: PANE_LOAD_SUCCESS,
          pane,
          result: track,
        })
        return NavigationService.push('/')
      })
      .catch(error => {
        return dispatch({
          type: PANE_LOAD_FAIL,
          error,
          pane: { ...pane, index: 0 },
        })
      })
  }
}
/**
 * Opens a playlist route in the explorer.
 * @param playlistId - The ID of the playlist to open
 * @param groupId - Optional group ID for shared playlists
 * @returns Thunk that opens the playlist route
 */
export function openPlaylistRoute(
  playlistId: PlaylistId,
  groupId?: GroupId,
): (dispatch: any, getState: GetState) => Promise<any> {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const pane = {
      name: '',
      index: 1,
      type: PLAYLIST_PANE,
    }

    try {
      dispatch({
        type: PANE_LOAD,
        pane: {
          ...placeholderSearchPane,
          url: groupId ? `${window.host}/api/browse/?type=playlist` : undefined,
        },
      })
      const [playlistResponse, group] = await Promise.all([
        api.getLastPlaylistVersionByPlaylistId(
          playlistId,
          groupId
            ? {
                share: 1,
              }
            : undefined,
        ),
        groupId ? api.getGroup(groupId) : null,
      ])
      const playlist = mapResponseToReducerObjects(
        dispatch,
        getState,
        playlistResponse,
      )

      if (groupId && group) {
        const groupSearchResponse = await api.getPaneView({
          groupHeader: JSON.stringify([groupId]),
          groupType: group.group_type,
        })
        const searchResults = mapResponseToReducerObjects(
          dispatch,
          getState,
          groupSearchResponse,
        ).results.filter(
          result =>
            result.type !== PLAYLIST ||
            result.playlist_id !== playlist.playlist_id,
        )
        const groupPane = {
          ...placeholderSearchPane,
          name: group.name,
          groupId: group.id,
          groupType: group.group_type,
          url: `${window.host}/api/browse/?type=playlist`,
        }
        const { id, name, image, group_type, url } = group
        dispatch({
          type: LIBRARY_SELECT,
          library: {
            id,
            name,
            image,
            group_type,
            url,
            panes: [groupPane],
          },
        })
        const playlistBelongsToGroup = playlist.groups.indexOf(url) > -1
        // don't show playlist if it was removed from inbox
        const results = playlistBelongsToGroup
          ? [playlist, ...searchResults]
          : searchResults
        dispatch({
          type: PANE_LOAD_SUCCESS,
          result: { ...groupSearchResponse, results },
          pane: playlistBelongsToGroup
            ? groupPane
            : { ...groupPane, activeItem: null },
        })

        if (playlistBelongsToGroup) {
          dispatch({
            type: PANE_LOAD_SUCCESS,
            pane,
            result: playlist,
          })
        }
      } else {
        dispatch({
          type: PANE_LOAD_SUCCESS,
          result: {
            results: [playlist],
          },
          pane: {
            ...placeholderSearchPane,
            name: playlist.name,
            url: playlist.url,
          },
        })
        dispatch({
          type: PANE_LOAD_SUCCESS,
          pane,
          result: playlist,
        })
      }

      return NavigationService.push('/')
    } catch (error) {
      dispatch({
        type: PANE_LOAD_FAIL,
        error,
        pane: { ...pane, index: 0 },
      })
      return NavigationService.push('/')
    }
  }
}
/**
 * Opens a group link and loads a placeholder pane.
 * @returns Thunk that opens the group link
 */
export function openGroupLink(): (dispatch: any) => Promise<void> {
  return async (dispatch: (...args: Array<any>) => any) => {
    dispatch({
      type: PANE_LOAD,
      pane: { ...placeholderSearchPane },
    })
  }
}
/**
 * Appends tracks to a playlist.
 * @param playlist - The playlist to append tracks to
 * @param tracks - Array of tracks to append
 * @returns Thunk that appends tracks to the playlist
 */
export function appendTracksToPlaylist(
  playlist: Playlist,
  tracks: Track[],
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const uploadedTracks = tracks.filter(t => t.id)
    const parentWarningTrack = (playlist.tracks ?? []).find(id =>
      // @ts-expect-error - TODO: fix this
      tracks.some(track => track.nesting_parent_id === id),
    )

    if (parentWarningTrack && playlist.include_nested_track_versions) {
      const nestedTrack = tracks.find(
        // @ts-expect-error - TODO: fix this
        track => track.nesting_parent_id === parentWarningTrack,
      )
      if (!nestedTrack) return
      NavigationService.push(
        `/explorer/tracks/${nestedTrack.id}/nesting-validation`,
        {
          state: {
            type: NESTED_TRACK_ALREADY_IN_PLAYLIST,
            track: nestedTrack.id,
            duplicateTrack: parentWarningTrack,
          },
        },
      )
      return
    }

    api
      .appendTracksToPlaylist(playlist, uploadedTracks)
      .then(response => {
        dispatch(updatePlaylists([response]))
        const updatedTracks = uploadedTracks.filter(
          t => t.playlists && !t.playlists.includes(playlist.playlist_id),
        )
        const tracksUpdate = updatedTracks.map(t => ({
          ...t,
          playlists: [playlist.playlist_id, ...(t.playlists || [])],
        }))
        mapResponseToReducerObjects(dispatch, getState, tracksUpdate)
      })
      .catch(error => {
        pushUINotification({
          message: 'Sorry! Could not add track.',
          level: 'error',
        })
        dispatch(sendGenericError(error))
      })
    const reducerPlaylist = getState().playlists[playlist.playlist_id]

    // prepare optimistic update for playlist, cause server can't return
    // response in sensible time
    if (
      reducerPlaylist.tracks &&
      reducerPlaylist.tracks.length > 0 &&
      reducerPlaylist.tracks_info &&
      reducerPlaylist.tracks_info.next === null
    ) {
      const trackIds = uploadedTracks
        .map(track => track.id)
        .filter(id => !reducerPlaylist.tracks.includes(id))

      if (trackIds.length > 0) {
        const updatedPlaylist = {
          ...playlist,
          tracks: [...reducerPlaylist.tracks, ...trackIds],
        }
        dispatch(updatePlaylists([updatedPlaylist]))
      }
    }

    pushUINotification({
      message: `Added to playlist!`,
      level: 'success',
    })
  }
}
/**
 * Removes tracks from a playlist.
 * @param playlist - The playlist to remove tracks from
 * @param tracks - Array of tracks to remove
 * @returns Thunk that removes tracks from the playlist
 */
export function removeTracksFromPlaylist( // TODO: add playlist pane type
  playlist: { playlist_id: PlaylistId },
  tracks: Track[],
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    api
      .removeTracksFromPlaylist(playlist, tracks)
      .then(response => {
        // @ts-expect-error - TODO: fix this
        mapResponseToReducerObjects(dispatch, getState, response)
        // this endpoint is used only for inboxes currently, you may need
        // to change notification logic later if it will be used elsewhere
        // pass message param or something
        pushUINotification({
          message: 'Removed from inbox!',
          level: 'success',
        })
      })
      .catch(error => dispatch(sendGenericError(error)))
  }
}
/**
 * Sends similarity feedback for a track.
 * @param result_track_id - The ID of the result track
 * @param feedback_type - The type of feedback
 * @param search_track_id - Optional search track ID
 * @param search_youtube_url - Optional YouTube URL
 * @returns Thunk that sends the similarity feedback
 */
export function sendSimilarityFeedback(
  result_track_id: TrackId,
  feedback_type: SIMILARITY_FEEDBACK,
  search_track_id?: TrackId,
  search_youtube_url?: string,
): (dispatch: any) => void {
  return (dispatch: (...args: Array<any>) => any) => {
    api
      .sendSimilarityFeedback({
        result_track_id,
        feedback_type,
        query_track_id: search_track_id,
        query_youtube_url: search_youtube_url,
      })
      .then(() => {
        pushUINotification({
          message: 'Feedback sent!',
          level: 'success',
        })
      })
      .catch(error => {
        pushUINotification({
          message: 'Sorry! Failed to send feedback.',
          level: 'error',
        })
        dispatch(sendGenericError(error))
      })
  }
}
// updates user settings on frontend without waiting server confirmation,
// use it on your own risk
/**
 * Updates user settings without waiting for server confirmation.
 * Use with caution as it updates the frontend immediately.
 * @param settings - The settings to update
 * @returns Thunk that updates user settings
 */
export function updateUserSettingsUnsafe(settings: Settings): Thunk {
  return dispatch => {
    const result = { ...settings }
    dispatch({
      type: USER_SETTINGS_UPDATE_SUCCESS,
      result: result,
    })
    api
      .updateUserSettings(settings)
      .catch(error => dispatch(sendGenericError(error)))
  }
}
// TODO: call adjustPlaylistPositionInExplorerPane on delete playlist
// TODO: create and call adjustTrack on playlist change
/**
 * Adjusts the position of a playlist in explorer panes after updates.
 * @param playlist - The playlist whose position to adjust
 * @returns Thunk that adjusts the playlist position in explorer panes
 */
export function adjustPlaylistPositionInExplorerPane(
  playlist: Playlist,
): (dispatch: any, getState: GetState) => void {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const { panes } = getState().explorer
    panes.forEach(pane => {
      if (
        pane.groupId &&
        pane.playlistSortDirection === SORT_DIRECTION_DESC &&
        pane.playlistSortParam === PLAYLIST_DATE_SORT &&
        pane.view !== TRACK_PANE &&
        pane.view !== ALBUM_VIEW
      ) {
        const paneResults = pane.results || []
        const playlistIndexInPane =
          paneResults.findIndex(
            ({ playlist_id }) => playlist_id === playlist.playlist_id,
          ) || -1

        if (playlistIndexInPane >= 0) {
          const insertIndex = paneResults.filter(
            item => item.type === GROUP,
          ).length
          const updateResults = [
            ...paneResults.slice(0, insertIndex),
            playlist,
            ...paneResults
              .slice(insertIndex)
              .filter(
                ({ playlist_id }) => playlist_id !== playlist.playlist_id,
              ),
          ]
          const CHUNK = 50
          const resultsWithoutGroups = updateResults.filter(
            item => item.type !== GROUP,
          )

          if (
            resultsWithoutGroups.length > CHUNK &&
            resultsWithoutGroups.length % CHUNK > 0 &&
            pane.next
          ) {
            // Remove the last element so that there are no duplicates when loading the next page.
            updateResults.pop()
          }

          let activeItem = pane.activeItem

          // Getting a new correct activeItem
          if (pane.activeItem !== undefined && pane.results) {
            // TODO: refactor activeItem splitting
            const activeItemPrefix = pane.activeItem?.split('/')[0] || ''
            const activeItemIndex = updateResults.findIndex(
              ({ id }) => String(id) == pane.activeItemId,
            )
            activeItem = `${activeItemPrefix}/${activeItemIndex}`
          }

          dispatch(updatePane({ ...pane, activeItem, results: updateResults }))
        }
      }
    })
  }
}
/**
 * Loads the default panel based on user settings.
 * @returns Thunk that loads the default panel
 */
export const loadDefaultPanel = (): ((
  dispatch: any,
  getState: GetState,
) => void) => {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (isInternalUrlRedirect()) return
    const lib = getDefaultLibrary(getState())
    if (lib) dispatch(loadLibrary(lib))
  }
}
/**
 * Opens a folder in the explorer.
 * @param pane - The current pane
 * @param itemIndex - The index of the folder item
 * @param group - The group representing the folder
 * @returns Thunk that opens the folder
 */
export const openFolder =
  (
    pane: ExplorerPane,
    itemIndex: number,
    group: Group,
  ): ((dispatch: any) => void) =>
  (dispatch: (...args: Array<any>) => any) => {
    dispatch(resetSelection())
    const { name, id } = group
    dispatch(updatePane({ ...pane, activeItem: `pane/${itemIndex}` }))
    dispatch(
      loadPane({
        url: `${window.host}/api/browse/`,
        name: name,
        index: pane.index + 1,
        groupId: id,
        groupType: CUSTOM_CHANNEL,
        view: DEFAULT_VIEW,
        type: SEARCH_RESULTS_PANE,
      } as ExplorerPane),
    )
  }

/**
 * Opens a brief in the explorer. This function handles two scenarios:
 * 1. If called from within the briefs list (pane and itemIndex provided), opens the brief directly
 * 2. If called from outside (e.g., notifications, search), loads briefs first then opens the brief
 *
 * @param pane - The explorer pane (null if calling from outside briefs)
 * @param itemIndex - The index of the brief in the pane (null if calling from outside briefs)
 * @param brief - The brief to open
 * @param skipUrlUpdate - Whether to skip updating the URL
 * @param skipBriefsLoad - Whether to skip loading briefs (useful when in project context)
 *
 * @example
 * // From within briefs list (fast path)
 * dispatch(openBrief(briefsPane, briefIndex, brief))
 *
 * // From outside briefs (e.g., notification click)
 * dispatch(openBrief(null, null, brief))
 *
 * // From project context (skip briefs load)
 * // Pane structure: [0] briefs/projects list, [1] project contents, [2] brief contents
 * dispatch(openBrief(null, null, brief, true, true))
 */
export const openBrief =
  (
    pane: ExplorerPane | null,
    itemIndex: number | null,
    brief: BriefListApiItem | Brief,
    skipUrlUpdate: boolean = false,
    skipBriefsLoad: boolean = false,
    activateByPlaylistId?: PlaylistId,
  ): ((dispatch: any, getState: GetState) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // If we have pane and itemIndex, it means we're already in briefs - open directly
    if (pane && typeof itemIndex === 'number') {
      await dispatch(
        openBriefDirectly(
          pane,
          itemIndex,
          brief,
          skipUrlUpdate,
          activateByPlaylistId,
        ),
      )
      return
    }

    // If we don't have pane/itemIndex and we're not skipping briefs load, load briefs first
    if (!skipBriefsLoad) {
      await dispatch(loadBriefs())
    }

    // Get the updated state and find the brief pane
    const updatedState = getState()
    const briefsPane = getExplorerFirstPane(updatedState)

    if (skipBriefsLoad) {
      // When skipping briefs load (project context), we need to use the second pane (project contents)
      // Pane structure: [0] briefs/projects list, [1] project contents, [2] brief contents
      const projectContentsPane = getExplorerPane(updatedState, { index: 1 })
      if (projectContentsPane && projectContentsPane.results) {
        // Find the brief in the project contents pane results
        const briefIndex = projectContentsPane.results.findIndex(
          (item: any) => item.type === 'brief' && item.id === brief.id,
        )
        await dispatch(
          openBriefDirectly(
            projectContentsPane,
            briefIndex,
            brief,
            skipUrlUpdate,
            activateByPlaylistId,
          ),
        )
      }
      return
    }

    if (briefsPane?.type === BRIEF_LIST_PANE && briefsPane.results) {
      const briefIndex = briefsPane.results.findIndex(
        (item: any) => item.type === 'brief' && item.id === brief.id,
      )
      await dispatch(
        openBriefDirectly(
          briefsPane,
          briefIndex,
          brief,
          skipUrlUpdate,
          activateByPlaylistId,
        ),
      )
      return
    }
  }

const openBriefDirectly =
  (
    pane: ExplorerPane,
    itemIndex: number,
    brief: BriefListApiItem | Brief,
    skipUrlUpdate: boolean = false,
    activateByPlaylistId?: PlaylistId,
  ): ((dispatch: any) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    dispatch(resetSelection())
    const itemPrefix = pane.view || 'pane'
    dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${itemIndex}` }))
    dispatch({
      type: LIBRARY_SELECT,
      library: {
        id: brief.inbox,
        name: brief.name,
        group_type: BRIEF_INBOX,
      },
    })

    // Update URL without triggering router navigation
    if (!__REACT_NATIVE__ && !skipUrlUpdate) {
      // Check if we're opening a brief within a project context
      const briefProjectId = pane.meta?.briefProjectId
      const url = briefProjectId
        ? `/briefs/projects/${briefProjectId}/${brief.id}`
        : `/briefs/${brief.id}`
      window.history.pushState({}, '', url)
    }

    const briefInboxPane = {
      index: pane.index + 1,
      name: brief.name,
      url: api.getPaneUrl({
        view: BRIEF_INBOX_VIEW,
        sortParam: PLAYLIST_DATE_SORT,
        sortDirection: SORT_DIRECTION_DESC,
        searchTerm: brief.id.toString(),
      }),
      groupId: brief.inbox,
      groupType: BRIEF_INBOX,
      view: BRIEF_INBOX_VIEW,
      type: SEARCH_RESULTS_PANE,
      meta: {
        submissions: brief.response_count,
        briefId: brief.id,
        briefProjectId: brief.project?.id,
        activateByPlaylistId,
      },
    }

    // skip calling API if there are no submissions, display empty brief message
    if (brief.response_count === 0) {
      return dispatch({
        type: PANE_LOAD_SUCCESS,
        pane: {
          ...briefInboxPane,
          error: null,
          loading: false,
          loaded: true,
          facet: [],
          folders: [],
          results: [],
        },
      })
    }

    await dispatch(loadPane(briefInboxPane))
  }

/**
 * Adds tracks to the auto-tagging channel.
 * @param tracks - Array of tracks to add
 * @returns Thunk that adds tracks to the auto-tagging channel
 */
export const addTracksToAutoTaggingChannel =
  (tracks: Track[]): ((dispatch: any, getState: GetState) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    try {
      const autoTaggingChannel = getAutoTaggingGroup(getState())
      if (!autoTaggingChannel) return
      return await dispatch(alterTrackGroups(tracks, [autoTaggingChannel]))
    } catch (error) {
      dispatch(sendGenericError(error))
    }
  }
/**
 * Configuration for stem separation operations.
 */
type StemSeparationConfig = {
  outputType: 'instrumental' | 'vocals'
  pendingKey: 'instant_instrumental_pending' | 'instant_a_cappella_pending'
  notificationPrefix: string
  outputName: string
}

const STEM_CONFIGS: Record<'instrumental' | 'vocals', StemSeparationConfig> = {
  instrumental: {
    outputType: 'instrumental',
    pendingKey: 'instant_instrumental_pending',
    notificationPrefix: 'instant-instrumental',
    outputName: 'Instrumental',
  },
  vocals: {
    outputType: 'vocals',
    pendingKey: 'instant_a_cappella_pending',
    notificationPrefix: 'instant-a-cappella',
    outputName: 'A cappella',
  },
}

/**
 * Module-level map of trackId → pending timeout handle.
 * Allows the WebSocket handler to cancel the fallback when the server push arrives.
 */
const stemSeparationPendingTimeouts = new Map<
  TrackId,
  ReturnType<typeof setTimeout>
>()
const STEM_SEPARATION_PENDING_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Cancels the fallback pending-state timeout for a given track.
 * Should be called by the WebSocket notification handler when the server push arrives.
 * @param trackId - The ID of the track whose timeout to cancel
 */
export const clearStemSeparationPendingTimeout = (trackId: TrackId): void => {
  const timeoutId = stemSeparationPendingTimeouts.get(trackId)
  if (timeoutId !== undefined) {
    clearTimeout(timeoutId)
    stemSeparationPendingTimeouts.delete(trackId)
  }
}

export const clearAllStemSeparationPendingTimeouts = (): void => {
  stemSeparationPendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId))
  stemSeparationPendingTimeouts.clear()
}

/**
 * Creates a stem separation version of a track (instrumental or vocals).
 * @param trackId - The ID of the track to process
 * @param config - Configuration for the stem separation type
 * @returns Thunk that creates the stem separation
 */
const createStemSeparation = (
  trackId: TrackId,
  config: StemSeparationConfig,
): ((dispatch: any, getState: GetState) => Promise<void>) => {
  return async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // TODO: add nesting validation https://disco-team.atlassian.net/browse/DISCO-5066
    const track = getTrack(getState(), { id: trackId })
    if (!track) return

    const errorNotification: UINotification = {
      _replaceNotificationWithUID: `${config.notificationPrefix}-pending-${trackId}`,
      level: 'error',
      title: 'Something went wrong!',
      message: `We couldn't generate the ${config.outputName} for '${track.name}' at this point. Please try again later.`,
      autoDismiss: 10,
    }
    // Use "an" before vowel sounds (e.g., "an A Cappella", "an instrumental")
    const startsWithVowel = /^[aeiou]/i.test(config.outputName)
    const article = startsWithVowel ? 'an' : 'a'
    const pendingNotification: UINotification = {
      uid: `${config.notificationPrefix}-pending-${trackId}`,
      level: 'success',
      title: `${config.outputName} generating...`,
      message: `We're creating ${article} ${config.outputName.toLowerCase()} for '${
        track.name
      }'.`,
      dismissible: 'button',
    }

    if (track[config.pendingKey]) {
      pushUINotification(pendingNotification)
      return
    }

    try {
      dispatch({
        type: TRACKS_UPDATE,
        tracks: [{ ...track, [config.pendingKey]: true }],
      })
      pushUINotification(pendingNotification)
      const result = await api.createInstantInstrumental(
        [trackId],
        config.outputType,
      )

      const trackResult = result[trackId]
      if (!trackResult?.success) {
        const errReason = trackResult?.message
        if (errReason) {
          errorNotification.message = `We couldn't generate the ${config.outputName} for '${track.name}' at this point. ${errReason}`
        }
        throw new Error(errorNotification.message)
      }

      // Success — start the fallback timeout now that the server has confirmed the job.
      // Cancel any pre-existing timeout for this track before registering the new one.
      const existingTimeoutId = stemSeparationPendingTimeouts.get(trackId)
      if (existingTimeoutId !== undefined) clearTimeout(existingTimeoutId)
      stemSeparationPendingTimeouts.set(
        trackId,
        setTimeout(() => {
          stemSeparationPendingTimeouts.delete(trackId)
          const currentTrack = getTrack(getState(), { id: trackId })
          if (!currentTrack?.[config.pendingKey]) return
          dispatch({
            type: TRACKS_UPDATE,
            tracks: [{ ...currentTrack, [config.pendingKey]: false }],
          })
          pushUINotification(errorNotification)
        }, STEM_SEPARATION_PENDING_TIMEOUT_MS),
      )
    } catch (error) {
      dispatch(sendGenericError(error))
      pushUINotification(errorNotification)
      dispatch({
        type: TRACKS_UPDATE,
        tracks: [{ ...track, [config.pendingKey]: false }],
      })
    }
  }
}

/**
 * Creates an instant instrumental version of a track.
 * @param trackId - The ID of the track to create instrumental for
 * @returns Thunk that creates an instant instrumental
 */
export const createInstantInstrumental = (
  trackId: TrackId,
): ((dispatch: any, getState: GetState) => Promise<void>) => {
  return createStemSeparation(trackId, STEM_CONFIGS.instrumental)
}

/**
 * Creates an instant a cappella version of a track.
 * @param trackId - The ID of the track to create a cappella for
 * @returns Thunk that creates an instant a cappella
 */
export const createInstantACappella = (
  trackId: TrackId,
): ((dispatch: any, getState: GetState) => Promise<void>) => {
  return createStemSeparation(trackId, STEM_CONFIGS.vocals)
}

/**
 * Opens the privacy policy dialog if conditions are met.
 * @returns Thunk that opens the privacy policy dialog
 */
export const openPrivacyPolicyDialog = (): ((
  dispatch: any,
  getState: GetState,
) => void) => {
  return (_dispatch: (...args: Array<any>) => any, getState: GetState) => {
    if (isShowPrivacyPolicyDialog(getState())) {
      NavigationService.push('/privacy-policy')
    }
  }
}
/**
 * Opens a playlist by its ID using routing.
 * @param playlistId - The ID of the playlist to open
 * @param groupId - Optional group ID for inbox playlists
 * @returns Thunk that navigates to the playlist
 */
export function openPlaylistById(
  playlistId: PlaylistId,
  groupId?: GroupId,
): (dispatch: any) => any {
  return (_dispatch: (...args: Array<any>) => any) => {
    if (groupId) {
      return NavigationService.push(
        `/inboxes/${groupId}/playlists/${playlistId}`,
      )
    }

    return NavigationService.push(`/explorer/playlists/${playlistId}`)
  }
}

/**
 * Opens a brief submission by opening the brief first, then the specific playlist.
 * This function handles loading both the brief and playlist if they're not already loaded.
 *
 * @param briefId - The ID of the brief to open
 * @param playlistId - The ID of the brief submission to open
 *
 * @example
 * // Open a brief submission from a notification
 * dispatch(openBriefSubmission(123, 456))
 */
/**
 * Opens a brief submission by opening the brief first, then the specific playlist.
 * This function handles loading both the brief and playlist if they're not already loaded.
 *
 * Pane structure when in project context:
 * [0] briefs/projects list, [1] project contents, [2] brief submissions, [3] playlist
 *
 * @param briefId - The ID of the brief to open
 * @param playlistId - The ID of the brief submission to open
 * @param skipBriefsLoad - Whether to skip loading briefs (useful when in project context)
 * @param projectId - Optional project ID for correct URL generation (preferred over pane metadata)
 * @returns Thunk that opens the brief submission
 */
export const openBriefSubmission =
  (
    briefId: number,
    playlistId: PlaylistId,
    skipBriefsLoad: boolean = false,
    projectId?: number,
  ): ((dispatch: any, getState: GetState) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const brief = await dispatch(loadBrief(briefId)).unwrap()

    // First, open the brief (skip briefs load if we're in project context)
    // Pass the playlistId so the submissions pane can highlight it after loading
    await dispatch(
      openBrief(null, null, brief, true, skipBriefsLoad, playlistId),
    )

    // Get the current state to find the briefs pane
    const state = getState()
    let briefsPane = getExplorerFirstPane(state)

    // If we're skipping briefs load (project context), get the project contents pane instead
    if (skipBriefsLoad) {
      briefsPane = getExplorerPane(state, { index: 1 })
    }

    // Update URL after opening brief so we can check for project context
    if (!__REACT_NATIVE__) {
      // Use passed projectId first, then fallback to pane metadata
      const briefProjectId = projectId || briefsPane?.meta?.briefProjectId
      const targetUrl = briefProjectId
        ? `/briefs/projects/${briefProjectId}/${briefId}/submissions/${playlistId}`
        : `/briefs/${briefId}/submissions/${playlistId}`
      const currentUrl = window.location.pathname
      if (currentUrl !== targetUrl) {
        window.history.pushState({}, '', targetUrl)
      }
    }

    if (briefsPane?.type === BRIEF_LIST_PANE) {
      // Get the playlist from state
      let playlist = getPlaylist(state, { id: playlistId })

      // If playlist is not loaded, we need to get it from the API
      if (!playlist) {
        try {
          const playlistResponse = await api.getLastPlaylistVersionByPlaylistId(
            playlistId,
            null,
          )
          mapResponseToReducerObjects(dispatch, getState, playlistResponse)
          // Get the playlist from state after loading
          playlist = getPlaylist(getState(), { id: playlistId })

          if (!playlist) return
        } catch (error) {
          pushUINotification({
            message:
              'There was an issue opening the brief submission. It may have been deleted.',
            level: 'error',
            autoDismiss: 10,
          })
          dispatch(sendGenericError(error))
          captureError(error, { description: 'Error opening brief submission' })
          return
        }
      }

      // Get the submissions pane to pass its groupId to the playlist pane
      const updatedState = getState()
      const submissionsPane = getExplorerPane(updatedState, {
        index: briefsPane.index + 1,
      })

      const urlParams = playlist.url.includes('?') ? '&share=1' : '?share=1'
      // Open the playlist pane after the brief pane

      dispatch(
        loadPane({
          index: briefsPane.index + 2, // After brief submissions pane
          name: playlist.name,
          title: playlist.name,
          type: 'playlist',
          url: playlist.url + urlParams,
          playlist_id: playlist.playlist_id,
          meta: {
            briefId,
            inboxId: submissionsPane?.groupId,
          },
        }),
      )
    }
  }

/**
 * Opens a brief project and loads its briefs list.
 * @param pane - The current explorer pane (optional - if null, will load briefs first)
 * @param itemIndex - The index of the brief project in the current pane (optional)
 * @param project - The brief project data
 * @param skipUrlUpdate - Whether to skip updating the URL
 * @returns Thunk that opens the brief project
 *
 * @example
 * // From within briefs list (fast path)
 * dispatch(openBriefProject(briefsPane, projectIndex, project))
 *
 * // From outside briefs (e.g., notification click)
 * dispatch(openBriefProject(null, null, project))
 */
export const openBriefProject =
  (
    pane: ExplorerPane | null,
    itemIndex: number | null,
    project: BriefProject,
    skipUrlUpdate: boolean = false,
  ): ((dispatch: any, getState: GetState) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // If we have pane and itemIndex, it means we're already in briefs - open directly
    if (pane && typeof itemIndex === 'number') {
      dispatch(
        openBriefProjectDirectly(pane, itemIndex, project, skipUrlUpdate),
      )
      return
    }

    // If we don't have pane/itemIndex, we need to load briefs first
    await dispatch(loadBriefs())

    // After loading briefs, get the updated state and find the brief project
    const updatedState = getState()
    const briefsPane = getExplorerFirstPane(updatedState)

    if (briefsPane?.type === BRIEF_LIST_PANE && briefsPane.results) {
      const projectIndex = briefsPane.results.findIndex(
        (item: any) => item.type === 'brief_project' && item.id === project.id,
      )
      await dispatch(
        openBriefProjectDirectly(
          briefsPane,
          projectIndex,
          project,
          skipUrlUpdate,
        ),
      )
      return
    }
  }

const openBriefProjectDirectly =
  (
    pane: ExplorerPane,
    itemIndex: number,
    project: BriefProject,
    skipUrlUpdate: boolean = false,
  ): ((dispatch: any) => Promise<void>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    dispatch(resetSelection())
    const itemPrefix = pane.view || 'pane'
    dispatch(updatePane({ ...pane, activeItem: `${itemPrefix}/${itemIndex}` }))

    // Update URL without triggering router navigation
    if (!__REACT_NATIVE__ && !skipUrlUpdate) {
      window.history.pushState({}, '', `/briefs/projects/${project.id}`)
    }

    await dispatch(
      loadPane({
        url: project.briefs_url,
        name: project.name,
        index: pane.index + 1,
        type: BRIEF_LIST_PANE,
        meta: {
          briefProjectId: project.id,
        },
      }),
    )
  }

/**
 * Updates the briefs and brief projects in the state
 * @param results - The API response results containing briefs and brief projects
 * @returns Thunk that updates the state with mapped results containing briefs and brief projects
 */
const setBriefsAndBriefProjects =
  (results: MixedBriefListApiItem[]): ((dispatch: any) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any) => {
    const mappedResults = results.map(data => ({
      ...data,
      type: data.type ?? 'brief',
    }))

    const briefProjects: BriefProject[] = []
    const briefs: Brief[] = []

    mappedResults.forEach(brief => {
      if (brief.type === 'brief') {
        briefs.push(brief as unknown as Brief)
      }
      if (brief.type === 'brief_project') {
        briefProjects.push(brief as BriefProject)
      }
    })

    dispatch(setBriefProjects(briefProjects))
    dispatch(setBriefs(briefs))

    return mappedResults
  }

/**
 * Updates Recents pane of specified item type with new item
 * @param item - The item to add to the recents pane
 * @param itemType - The type of the item being added
 * @returns Thunk that updates the recents pane
 */
export function updateRecentsWithNewItem(
  item: Track | Playlist | Group,
  itemType: 'track' | 'playlist' | 'album' | 'group',
): (dispatch: any) => void {
  return (dispatch: (...args: any[]) => any) => {
    dispatch({
      type: `@RECENTS/${PANE_RECENTS_PUSH}`,
      item,
      itemType,
    })
  }
}
